import { Injectable } from "@angular/core";
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Apollo } from "apollo-angular";
import { TokenRefreshLink } from 'apollo-link-token-refresh';
import { createUploadLink } from 'apollo-upload-client';
import { default as JWT, default as jwtDecode } from 'jwt-decode';
import { map } from 'rxjs/operators';
import { UserContextModel, UserInfoModel } from "../models/user-context.model";
import { RoutingUrl } from './../app/shared/enum/routing-url.enum';
import { environment } from './../environments/environment';
import { GetAccountInfoGQL, LoginGQL, LogoutGQL, SoftwareCode, VerifyAccessTokenGQL } from './../generated/accountgql';
import { ApolloLink, Observable, InMemoryCache } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { SocketIoService } from './socketio.service';

export enum StorageType {
    ACCESS = "JwtAccessToken",
    USER_INFO = "UserInfo"
}

@UntilDestroy()
@Injectable({ providedIn: "root" })
export class AuthenticationService {
    userContext: UserContextModel;
    userInfo = {} as UserInfoModel;

    apiNum: number = 0;

    constructor(
        private _loginGql: LoginGQL,
        private _logoutGql: LogoutGQL,
        private _router: Router,
        private _snackBar: MatSnackBar,
        private _apollo: Apollo,
        private _getAccountInfoGQL: GetAccountInfoGQL,
        private verifyAccessTokenGQL: VerifyAccessTokenGQL,
        private _socketService: SocketIoService,
        ) { }    

    login(username: string, password: string) {

        sessionStorage.clear();

        this._loginGql.mutate({
            LoginId: username,
            Password: password
        }).pipe(map(x => x.data.loginWithUsername), untilDestroyed(this)).subscribe(data => {

            this.setStorageItem(data.accessToken);
            this._getTokenDetails();
            this._setUserInfo();

        }, err => {
            this._snackBar.open(err.graphQLErrors[0].message, 'Dismiss', { duration: 2500 });
        });
    }

    logout() {
        this._logoutGql.mutate().pipe(map(x => x.data.logout), untilDestroyed(this)).subscribe(data => {
            if (data) {
                sessionStorage.clear();
                this._router.navigate([RoutingUrl.HOME]);
            }
        }, err => {
            sessionStorage.clear();
            this._snackBar.open(err.graphQLErrors[0].message, 'Dismiss', { duration: 2500 });
        });
        this._socketService.socketDisconnection();
    }

    isLoggedIn() {
        this._getTokenDetails();
        /* 
            check token software code
        */
       if(this.userContext?.softwareCode != SoftwareCode.LeaseTodo) return false;

        // remove client before creating new one
        if (this.userContext) {
            this._apollo.removeClient();
            this._apollo.createDefault({
                link: this.createApolloLink(this.userContext.apiGatewayUrl),
                cache: new InMemoryCache(),
                defaultOptions: {
                    watchQuery: {
                        fetchPolicy: "no-cache",
                    },
                    query: {
                        fetchPolicy: "no-cache",
                    },
                    mutate: {
                        fetchPolicy: "no-cache",
                    },
                },
            });

            // check expiry
            return !this._isTokenExpired();
        }

        return false;
    }

    private _getStorageItem(key?: any) {
        return sessionStorage.getItem(key || StorageType.ACCESS);
    }

    setStorageItem(item: string, key?: any) {
        sessionStorage.setItem(key || StorageType.ACCESS, item);
    }

    private _isTokenExpired() {
        const expireTime = new Date(this.userContext.exp * 1000);
        return expireTime < new Date();
    }

    private _getTokenDetails() {
        this.userContext = null;
        this.userInfo = new UserInfoModel();

        const token = this._getStorageItem();
        const _userInfo = this._getStorageItem(StorageType.USER_INFO);

        if (token) {
            const decoded = JWT(token);

            this.userContext = {
                userId: decoded['userID'],
                softwareId: decoded['softwareID'],
                accountId: decoded['accountID'],
                tokenVersion: decoded['tokenVersion'],
                softwareCode: decoded['softwareCode'],
                apiGatewayUrl: decoded['apiGatewayUrl'],
                lUserId: decoded['entityID'],
                lCompanyId: decoded['lCompanyId'],
                iat: decoded['iat'],
                exp: decoded['exp'],
                isSuperUser: decoded['isSuperUser']
            };
        }

        /*
         added on 24-Sep-2020
         to get subheader info
         */
        if (_userInfo) {
            this.userInfo = <UserInfoModel>JSON.parse(_userInfo);
        } else {
            this._setUserInfo();
        }
    }

    private _setUserInfo() {
        if (this.userContext) {
            this._getAccountInfoGQL.fetch({
                ID: this.userContext.accountId,
                settingKey: 'tenant.isPaymentEnabled'
            }).pipe(map(x => x.data), untilDestroyed(this)).subscribe(result => {

                // ES6 clone array
                this.userInfo.user = [...result.getUser];
                this.userInfo.account = result.getAccountInfo.account;

                this.userInfo.account.settings = {
                    currencyCode: result.getCurrencyByAccountId.currency?.symbol ? `${result.getCurrencyByAccountId.currency?.symbol}  ` : 'RM ',
                    currencySymbol: result.getCurrencyByAccountId.currency?.symbol ?? 'RM ',
                    isPaymentEnabled: result.getAccountSettings.length > 0 ? result.getAccountSettings[0].settingValue.toLowerCase() == 'true' : false
                }

                this.setStorageItem(JSON.stringify(this.userInfo), StorageType.USER_INFO);

                this._router.navigate([RoutingUrl.PROPERTY_LISTING]);
            });
        }
    }

    createApolloLink(targetApiUrl: string) {
        const accountApi = environment.AccountApiHost;
        let loading = $('#showLoading');

        const requestLink = new ApolloLink(
            (operation, forward) =>
                new Observable(observer => {
                    let handle: any;
                    Promise.resolve(operation)
                        .then(opr => {
                            const accessToken = this._getStorageItem();
                            loading.css("display", "block");
                            this.apiNum++;
                            if (accessToken) {
                                opr.setContext({
                                    headers: {
                                        authorization: `Bearer ${accessToken}`,
                                    },
                                });
                            }
                        })
                        .then(() => {
                            handle = forward(operation).subscribe({
                                next: observer.next.bind(observer),
                                error: observer.error.bind(observer),
                                complete: observer.complete.bind(observer),
                            });
                        })
                        .catch(observer.error.bind(observer));

                    return () => {
                        if (handle) { handle.unsubscribe(); }
                    };
                })
        );

        
        const responeseData = new ApolloLink((operation, forward) => {
            return forward(operation).map(response => {
                if (this.apiNum > 0){
                    --this.apiNum;
                } 
                let resultResponse = JSON.parse(JSON.stringify(response));
                if (this.apiNum == 0) {
                    loading = $('#showLoading');
                    loading.css("display", "none");
                }
                return resultResponse;
            });
        });


        return ApolloLink.from([
            new TokenRefreshLink({
                accessTokenField: 'accessToken',
                isTokenValidOrUndefined: () => {

                    const token = this._getStorageItem();

                    // if doesn't have token at all, should just return true
                    // if return false will got to handleError()
                    if (!token) {
                        return true;
                    }

                    try {
                        // if have token then do decode to check expiry time
                        const { exp } = jwtDecode(token);
                        if (Date.now() >= exp * 1000) {
                            return false;
                        } else {
                            return true;
                        }
                    } catch {
                        return false;
                    }
                },
                fetchAccessToken: () => {
                    return fetch(`${accountApi}/refresh_token`, {
                        method: 'POST',
                        headers: {
                            'SoftwareCode': 'LEASE_TODO'
                        },
                        credentials: 'include',
                    });
                },
                handleFetch: accessToken => {
                    this.setStorageItem(accessToken);
                    this._getTokenDetails();
                    this._socketService.logUserOnlineStatus();
                },
                handleError: () => {
                    this._snackBar.open('Your refresh token is invalid. Try to relogin', 'Dismiss', { duration: 2500 });
                    this._router.navigate([RoutingUrl.LOGIN]);
                }
            }),
            onError(({ graphQLErrors,networkError }) => {
                // all graphQl error will be thrown here

                 //network error will navigate to error page
                 if (networkError) {
                    // console.log("Navigate to error page ");
                    this.apiNum = 0;
                    loading = $('#showLoading');
                    loading.css("display", "none");
                    this._router.navigate(['/authentication/error']);
                }

                // if jwt expired error thrown then just prompt user to try again
                if (graphQLErrors[0].message.toLowerCase().includes("expired".toLowerCase())) {
                    this._snackBar.open('Please try again', 'Dismiss', { duration: 2500 });
                } else {
                    // all other situtations should just throw any msg returned from API
                    this._snackBar.open(graphQLErrors[0].message, 'Dismiss', { duration: 2500 });
                }

                if (graphQLErrors[0].message.toLowerCase().includes("not authenticated".toLowerCase())
                || graphQLErrors[0].message.toLowerCase().includes("Token is not valid, please try login again".toLowerCase())
                || graphQLErrors[0].message.toLowerCase().includes("Token is blacklisted, please login again".toLowerCase())) {
                    this._socketService.socketDisconnection();
                    sessionStorage.clear();
                    this._router.navigate([RoutingUrl.HOME]);
            }
            }),
            requestLink,
            responeseData,
            createUploadLink({
                uri: targetApiUrl,
                credentials: 'include'
            })
        ]);
    }

    authorizeWithToken(token:string){
        this.verifyAccessTokenGQL.mutate({
            token:token
        }).subscribe(x => {
            sessionStorage.clear();
            this.setStorageItem(x.data.verifyAccessToken);
            this._getTokenDetails();
            this._setUserInfo();

        });
    }
}
